import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useNews } from "@/hooks/useNews";
import { deleteNews } from "@/utils/graphqlClient";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Loader2, ExternalLink, CheckCircle, Clock, Filter, Undo2, Globe, DollarSign, Layers, BarChart3, Tag, Trash2, ChevronDown, Settings, Search, ArrowUpDown } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { formatVersionDateUS } from '@/utils/dateUtils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import NewsViewerModal from "@/components/news/NewsViewerModal";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import DeleteConfirmationDialog from "@/components/common/DeleteConfirmationDialog";

interface NewsItem {
  id: string;
  slug: string;
  version: string;
  before_version?: string;
  quarter?: string;
  hook?: string;
  description?: string;
  before_image_url?: string;
  after_image_url?: string;
  gif_url?: string;
  published: boolean;
  verified: boolean;
  created_at?: string;
  logo_url?: string;
  price_change?: boolean;
  plan_change?: boolean;
  metric_change?: boolean;
  discount_change?: boolean;
  features_change?: boolean;
  assets?: Array<{
    id: string;
    collection_id: string;
    type: string;
    page_id: string;
    slug: string;
    version: string;
    title: string;
    description?: string;
    primary_image?: string;
    secondary_image?: string;
    primary_original_url?: string;
    primary_cropped_url?: string;
    primary_markup_url?: string;
    secondary_original_url?: string;
    secondary_cropped_url?: string;
    secondary_markup_url?: string;
    crop_aspect_ratio?: string;
    crop_coordinates?: string;
    header?: string;
    subheader?: string;
    comparison_mode?: string;
    secondary_version?: string;
    secondary_page_id?: string;
    primary_annotations?: string;
    secondary_annotations?: string;
    primary_viewport_state?: any;
    secondary_viewport_state?: any;
    created_at: string;
    updated_at: string;
    company_slug?: string;
    logo_url?: string;
  }>;
}

// Change type filter options
interface ChangeTypeFilter {
  key: keyof NewsItem;
  label: string;
  icon: React.ComponentType<any>;
  color: string;
}

const CHANGE_TYPE_FILTERS: ChangeTypeFilter[] = [
  { key: 'price_change', label: 'Price', icon: DollarSign, color: 'green' },
  { key: 'plan_change', label: 'Plan', icon: Layers, color: 'blue' },
  { key: 'metric_change', label: 'Metric', icon: BarChart3, color: 'purple' },
  { key: 'discount_change', label: 'Discount', icon: Tag, color: 'orange' },
  { key: 'features_change', label: 'Features', icon: Settings, color: 'indigo' },
];

const News = () => {
  const [selectedQuarter, setSelectedQuarter] = useState<string>("last_14_days");
  const [verifiedFilter, setVerifiedFilter] = useState<string>("all"); // "all", "verified", "not_verified"
  const [selectedChangeTypes, setSelectedChangeTypes] = useState<Set<string>>(new Set()); // Change type filters
  const [companySearchInput, setCompanySearchInput] = useState<string>(""); // Input field value (what user types)
  const [companyFilter, setCompanyFilter] = useState<string>(""); // Actual search filter (triggers API call)
  const [sortBy, setSortBy] = useState<string>("most_recent"); // "alphabetical" or "most_recent"
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50); // Items per page
  const [publishingItems, setPublishingItems] = useState<Set<string>>(new Set());
  const [deletingItems, setDeletingItems] = useState<Set<string>>(new Set());
  const [selectedNewsItem, setSelectedNewsItem] = useState<NewsItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [startInEditMode, setStartInEditMode] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<NewsItem | null>(null);
  const { toast } = useToast();

  // Calculate offset for pagination
  const offset = (currentPage - 1) * pageSize;

  // Convert verifiedFilter to boolean | null for the API
  // NOTE: Using null instead of undefined to ensure GraphQL client doesn't cache incorrectly
  const verifiedParam = verifiedFilter === "verified" ? true : verifiedFilter === "not_verified" ? false : null;

  // Convert selectedChangeTypes to individual boolean parameters
  const priceChangeParam = selectedChangeTypes.has('price_change') ? true : null;
  const planChangeParam = selectedChangeTypes.has('plan_change') ? true : null;
  const metricChangeParam = selectedChangeTypes.has('metric_change') ? true : null;
  const discountChangeParam = selectedChangeTypes.has('discount_change') ? true : null;
  const featuresChangeParam = selectedChangeTypes.has('features_change') ? true : null;

  // Fetch news with React Query, with ALL filters applied server-side
  const { data, isLoading, error, refetch } = useNews({
    quarter: selectedQuarter,
    slug: companyFilter.trim() || undefined,
    verified: verifiedParam,
    priceChange: priceChangeParam,
    planChange: planChangeParam,
    metricChange: metricChangeParam,
    discountChange: discountChangeParam,
    featuresChange: featuresChangeParam,
    limit: pageSize,
    offset: offset
  });

  // Process and enrich news data from React Query
  const rawNewsItems = data?.news || [];

  // Enrich news items with company logos
  const [enrichedNewsItems, setEnrichedNewsItems] = useState<NewsItem[]>([]);

  useEffect(() => {
    const enrichWithLogos = async () => {
      if (rawNewsItems.length === 0) return;

      // Extract base company slugs from composite slugs
      const uniqueSlugs = [...new Set(rawNewsItems.map(item => {
        const companySlug = item.slug.split('.')[0];
        return companySlug;
      }))];

      const { data: companies, error } = await supabase
        .from('companies')
        .select('slug, logo_url')
        .in('slug', uniqueSlugs);

      if (error) {
        console.error('Error fetching companies:', error);
      }

      // Add logo URLs to news items - match by base company slug
      const enriched = rawNewsItems.map(item => {
        const companySlug = item.slug.split('.')[0];
        return {
          ...item,
          logo_url: companies?.find(c => c.slug === companySlug)?.logo_url || null
        };
      });

      setEnrichedNewsItems(enriched as NewsItem[]);
    };

    enrichWithLogos();
  }, [rawNewsItems]);

  // Check for deep linking parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const itemId = urlParams.get('item');

    if (itemId && enrichedNewsItems.length > 0) {
      const item = enrichedNewsItems.find(i => i.id === itemId);
      if (item) {
        setSelectedNewsItem(item);
        setModalOpen(true);
        // Clear the URL parameter to prevent reopening on refresh
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('item');
        window.history.replaceState({}, '', newUrl.pathname + newUrl.search);
      }
    }
  }, [enrichedNewsItems]);

  // Get current quarter in YYYYQN format
  const getCurrentQuarter = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // getMonth() returns 0-11
    const quarter = Math.ceil(month / 3);
    return `${year}Q${quarter}`;
  };

  // Validation functions for diff entries
  const isValidCloudinaryUrl = (url?: string): boolean => {
    if (!url) return false;
    return url.includes('cloudinary.com');
  };

  const isValidDiffEntry = (item: NewsItem): boolean => {
    // Must have assets array with at least one asset
    if (!item.assets || item.assets.length === 0) {
      return false;
    }

    // Check each asset for valid asset_id and cloudinary URLs
    return item.assets.every(asset => {
      // Must have valid asset_id
      if (!asset.id) {
        return false;
      }

      // Must have both primary_original_url AND secondary_original_url
      if (!asset.primary_original_url || !asset.secondary_original_url) {
        return false;
      }

      // Both URLs must be cloudinary URLs
      if (!isValidCloudinaryUrl(asset.primary_original_url) ||
          !isValidCloudinaryUrl(asset.secondary_original_url)) {
        return false;
      }

      return true;
    });
  };

  // Filter function to apply validation to news items
  const filterValidDiffEntries = (items: NewsItem[]): NewsItem[] => {
    return items.filter(isValidDiffEntry);
  };

  // Helper function to check if a version date (YYYYMMDD) is within the last 14 days
  const isDateWithinLast14Days = (versionDate: string): boolean => {
    if (!versionDate || versionDate.length !== 8) {
      return false;
    }

    try {
      const year = parseInt(versionDate.substring(0, 4));
      const month = parseInt(versionDate.substring(4, 6)) - 1; // Month is 0-indexed in Date
      const day = parseInt(versionDate.substring(6, 8));

      const date = new Date(year, month, day);
      const today = new Date();
      const fourteenDaysAgo = new Date(today.getTime() - (14 * 24 * 60 * 60 * 1000));

      return date >= fourteenDaysAgo && date <= today;
    } catch (error) {
      console.error('Error parsing version date:', versionDate, error);
      return false;
    }
  };

  // Helper function to check if a version date (YYYYMMDD) is within the last month (30 days)
  const isDateWithinLastMonth = (versionDate: string): boolean => {
    if (!versionDate || versionDate.length !== 8) return false;

    try {
      const year = parseInt(versionDate.substring(0, 4));
      const month = parseInt(versionDate.substring(4, 6)) - 1; // Month is 0-indexed in Date
      const day = parseInt(versionDate.substring(6, 8));

      const date = new Date(year, month, day);
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));

      return date >= thirtyDaysAgo && date <= today;
    } catch (error) {
      console.error('Error parsing version date:', versionDate, error);
      return false;
    }
  };

  // Helper function to check if a version date (YYYYMMDD) is within the last N days
  const isDateWithinLastNDays = (versionDate: string, days: number): boolean => {
    if (!versionDate || versionDate.length !== 8) return false;

    try {
      const year = parseInt(versionDate.substring(0, 4));
      const month = parseInt(versionDate.substring(4, 6)) - 1; // Month is 0-indexed in Date
      const day = parseInt(versionDate.substring(6, 8));

      const date = new Date(year, month, day);
      const today = new Date();
      const nDaysAgo = new Date(today.getTime() - (days * 24 * 60 * 60 * 1000));

      return date >= nDaysAgo && date <= today;
    } catch (error) {
      console.error('Error parsing version date:', versionDate, error);
      return false;
    }
  };

  // Helper function to filter news items by last 14 days
  const filterByLast14Days = (items: NewsItem[]): NewsItem[] => {
    return items.filter(item => {
      const versionWithin14Days = isDateWithinLast14Days(item.version);

      // For a meaningful "14 days" filter, if before_version exists it should be within last 30 days
      // This ensures we're comparing recent changes, not changes from months ago
      if (item.before_version) {
        const beforeVersionWithin30Days = isDateWithinLastNDays(item.before_version, 30);
        return versionWithin14Days && beforeVersionWithin30Days;
      }

      // If no before_version, just check the main version is within 14 days
      return versionWithin14Days;
    });
  };

  // Helper function to filter news items by last month (30 days)
  const filterByLastMonth = (items: NewsItem[]): NewsItem[] => {
    return items.filter(item => {
      const versionWithinMonth = isDateWithinLastMonth(item.version);

      // For a meaningful "last month" filter, if before_version exists it should be within last 60 days
      // This ensures we're comparing reasonably recent changes
      if (item.before_version) {
        const beforeVersionWithin60Days = isDateWithinLastNDays(item.before_version, 60);
        return versionWithinMonth && beforeVersionWithin60Days;
      }

      // If no before_version, just check the main version is within 30 days
      return versionWithinMonth;
    });
  };

  // Sorting function for news items
  const sortNewsItems = (items: NewsItem[], sortType: string): NewsItem[] => {
    if (sortType === "alphabetical") {
      return [...items].sort((a, b) => a.slug.localeCompare(b.slug));
    } else if (sortType === "most_recent") {
      // Sort by before_version (most recent first)
      return [...items].sort((a, b) => {
        const beforeA = a.before_version || '';
        const beforeB = b.before_version || '';

        // If one doesn't have before_version, put it at the end
        if (!beforeA && beforeB) return 1;
        if (beforeA && !beforeB) return -1;
        if (!beforeA && !beforeB) return 0;

        // Sort by before_version in descending order (most recent first)
        return beforeB.localeCompare(beforeA);
      });
    }
    return items;
  };

  // Get unique quarters from enriched data for filter dropdown
  const availableQuarters = useMemo(() => {
    const quarters = [...new Set(enrichedNewsItems.map(item => item.quarter).filter(Boolean))];
    return quarters.sort((a, b) => {
      // Sort quarters in descending order (most recent first)
      const [yearA, quarterA] = a!.split('Q');
      const [yearB, quarterB] = b!.split('Q');
      if (yearA !== yearB) return parseInt(yearB!) - parseInt(yearA!);
      return parseInt(quarterB!) - parseInt(quarterA!);
    });
  }, [enrichedNewsItems]);

  // Filter valid diff entries (asset validation only)
  const validDiffEntries = useMemo(() => {
    const filtered = filterValidDiffEntries(enrichedNewsItems);
    return filtered;
  }, [enrichedNewsItems]);

  // Apply client-side sorting only (all filters now applied server-side)
  const newsItems = useMemo(() => {
    let result = [...validDiffEntries];

    // ALL FILTERS NOW APPLIED SERVER-SIDE:
    // - Quarter filter (last_14_days, last_month, specific quarters, or all)
    // - Verified filter (verified, not_verified, or all)
    // - Change type filters (price, plan, metric, discount, features)
    // - Company filter (slug search)

    // Apply sorting only (client-side for now)
    result = sortNewsItems(result, sortBy);

    return result;
  }, [validDiffEntries, sortBy]);

  // Simplified filter handlers - no need for applyFilters function with useMemo
  const handleQuarterChange = (quarter: string) => {
    setSelectedQuarter(quarter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleVerifiedFilterChange = (filter: string) => {
    setVerifiedFilter(filter);
    setCurrentPage(1);
  };

  const handleChangeTypeToggle = (changeType: string) => {
    const newSelectedChangeTypes = new Set(selectedChangeTypes);
    if (newSelectedChangeTypes.has(changeType)) {
      newSelectedChangeTypes.delete(changeType);
    } else {
      newSelectedChangeTypes.add(changeType);
    }
    setSelectedChangeTypes(newSelectedChangeTypes);
    setCurrentPage(1);
  };

  const handleCompanySearchInputChange = (value: string) => {
    setCompanySearchInput(value);
    // If user clears the text, immediately clear the filter
    if (value.trim() === "") {
      setCompanyFilter("");
      setCurrentPage(1);
    }
  };

  const executeCompanySearch = () => {
    setCompanyFilter(companySearchInput);
    setCurrentPage(1);
  };

  const handleCompanySearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      executeCompanySearch();
    }
  };

  const handleCompanySearchBlur = () => {
    // Only trigger search if the input value differs from the current filter
    if (companySearchInput.trim() !== companyFilter.trim()) {
      executeCompanySearch();
    }
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
  };

  // Pagination handlers
  const handleLoadMore = () => {
    if (data?.hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "No date";
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return "Invalid date";
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const formatVersionAsDate = (version: string) => {
    const formatted = formatVersionDateUS(version);
    return formatted || version;
  };

  const formatQuarter = (quarter: string) => {
    // Split quarter like "2024Q1" into "2024" and "Q1"
    if (quarter && quarter.includes('Q')) {
      const [year, q] = quarter.split('Q');
      return { year, quarter: `Q${q}` };
    }
    return { year: '', quarter: quarter };
  };

  const renderChangeTypeIndicators = (item: NewsItem, showLabels: boolean = false) => {
    const indicators = [];
    
    if (item.price_change) {
      indicators.push(
        <Tooltip key="price">
          <TooltipTrigger asChild>
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 ${showLabels ? '' : 'w-7 h-7 justify-center'}`}>
              <DollarSign className="h-3 w-3" />
              {showLabels && <span>Price</span>}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Price Change</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    
    if (item.plan_change) {
      indicators.push(
        <Tooltip key="plan">
          <TooltipTrigger asChild>
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ${showLabels ? '' : 'w-7 h-7 justify-center'}`}>
              <Layers className="h-3 w-3" />
              {showLabels && <span>Plan</span>}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Plan Change</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    
    if (item.metric_change) {
      indicators.push(
        <Tooltip key="metric">
          <TooltipTrigger asChild>
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 ${showLabels ? '' : 'w-7 h-7 justify-center'}`}>
              <BarChart3 className="h-3 w-3" />
              {showLabels && <span>Metric</span>}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Metric Change</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    
    if (item.discount_change) {
      indicators.push(
        <Tooltip key="discount">
          <TooltipTrigger asChild>
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 ${showLabels ? '' : 'w-7 h-7 justify-center'}`}>
              <Tag className="h-3 w-3" />
              {showLabels && <span>Discount</span>}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Discount Change</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    if (item.features_change) {
      indicators.push(
        <Tooltip key="features">
          <TooltipTrigger asChild>
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 ${showLabels ? '' : 'w-7 h-7 justify-center'}`}>
              <Settings className="h-3 w-3" />
              {showLabels && <span>Features</span>}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Features Change</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return indicators;
  };

  const openNewsViewer = (item: NewsItem, editMode: boolean = false) => {
    setSelectedNewsItem(item);
    setModalOpen(true);
    setStartInEditMode(editMode);
  };

  const handleNewsItemUpdate = (updatedItem: NewsItem) => {
    // Refetch data after update to get fresh data from server
    refetch();
    setSelectedNewsItem(updatedItem);
  };

  const publishNews = async (newsId: string, action: 'publish' | 'unpublish' = 'publish') => {
    setPublishingItems(prev => new Set(prev).add(newsId));

    try {
      const { data, error } = await supabase.functions.invoke('publish-news', {
        body: { news_id: newsId, action }
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || `Failed to ${action} news`);
      }

      // Refetch data after publish/unpublish
      refetch();

      toast({
        title: `News ${action}ed successfully`,
        description: action === 'publish'
          ? `${data.slug} - ${data.version} has been published to storage`
          : `${data.slug} - ${data.version} has been unpublished and removed from storage`,
      });

    } catch (error: any) {
      console.error(`Error ${action}ing news:`, error);
      toast({
        variant: "destructive",
        title: `${action.charAt(0).toUpperCase() + action.slice(1)} failed`,
        description: error.message || `Failed to ${action} news item`
      });
    } finally {
      setPublishingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(newsId);
        return newSet;
      });
    }
  };

  const handleDeleteClick = (item: NewsItem) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    const newsId = itemToDelete.id;
    setDeletingItems(prev => new Set(prev).add(newsId));

    try {
      const result = await deleteNews(newsId);

      if (!result.deleteNews?.success) {
        throw new Error(result.deleteNews?.message || 'Failed to delete news item');
      }

      // Refetch data after deletion
      refetch();

      toast({
        title: "News deleted successfully",
        description: result.deleteNews.message,
      });

    } catch (error: any) {
      console.error('Error deleting news:', error);
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error.message || "Failed to delete news item"
      });
    } finally {
      setDeletingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(newsId);
        return newSet;
      });
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  // Use server-provided counts for accurate totals regardless of pagination
  const publishedCount = data?.publishedCount || 0;
  const verifiedCount = data?.verifiedCount || 0;
  const totalCount = data?.totalCount || 0;

  // Calculate draft and unverified counts from the totals
  const draftCount = totalCount - publishedCount;
  const unverifiedCount = totalCount - verifiedCount;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500 text-lg">Error loading news items</p>
          <Button onClick={() => refetch()} className="mt-4">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Diffs</h1>
        {/* Stats row */}
        <div className="flex items-center gap-4 text-sm mb-4">
          {totalCount > 0 && (
            <>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-green-700 font-medium">{publishedCount} Published</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500" />
                <span className="text-orange-700 font-medium">{draftCount} Draft</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span className="text-emerald-700 font-medium">{verifiedCount} Verified</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-full border-2 border-gray-400"></div>
                <span className="text-gray-700 font-medium">{unverifiedCount} Unverified</span>
              </div>
              <div className="text-gray-500">
                Total: {totalCount} items
              </div>
            </>
          )}
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-4 flex-wrap">
            {/* Company Filter */}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Filter by company..."
                value={companySearchInput}
                onChange={(e) => handleCompanySearchInputChange(e.target.value)}
                onKeyDown={handleCompanySearchKeyDown}
                onBlur={handleCompanySearchBlur}
                className="w-[200px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={executeCompanySearch}
                className="flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                Search
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select value={selectedQuarter} onValueChange={handleQuarterChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select quarter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_14_days">Last 14 days</SelectItem>
                  <SelectItem value="last_month">Last month</SelectItem>
                  <SelectItem value="all">All Quarters</SelectItem>
                  {availableQuarters.map((quarter) => (
                    <SelectItem key={quarter} value={quarter}>
                      {quarter}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-gray-500" />
              <Select value={verifiedFilter} onValueChange={handleVerifiedFilterChange}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Filter verified" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  <SelectItem value="verified">Verified Only</SelectItem>
                  <SelectItem value="not_verified">Not Verified</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Change Type Filter */}
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    {selectedChangeTypes.size > 0 ? `Changes (${selectedChangeTypes.size})` : 'Change Types'}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Filter by Change Type</DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  {CHANGE_TYPE_FILTERS.map((changeTypeFilter) => {
                    const Icon = changeTypeFilter.icon;
                    const isSelected = selectedChangeTypes.has(changeTypeFilter.key);

                    // Static color classes to avoid dynamic class generation issues
                    const colorClasses = {
                      green: 'bg-green-100 text-green-600',
                      blue: 'bg-blue-100 text-blue-600',
                      purple: 'bg-purple-100 text-purple-600',
                      orange: 'bg-orange-100 text-orange-600',
                      indigo: 'bg-indigo-100 text-indigo-600',
                    };

                    return (
                      <DropdownMenuItem
                        key={changeTypeFilter.key}
                        onClick={() => handleChangeTypeToggle(changeTypeFilter.key)}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => {}} // Controlled by onClick above
                          className="pointer-events-none"
                        />
                        <div className={`p-1 rounded-full ${colorClasses[changeTypeFilter.color as keyof typeof colorClasses]}`}>
                          <Icon className="h-3 w-3" />
                        </div>
                        <span className="flex-1">{changeTypeFilter.label}</span>
                      </DropdownMenuItem>
                    );
                  })}

                  {selectedChangeTypes.size > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedChangeTypes(new Set());
                          setCompanySearchInput("");
                          setCompanyFilter("");
                          setSortBy("most_recent");
                          setCurrentPage(1);
                        }}
                        className="text-gray-600"
                      >
                        Clear all filters
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="most_recent">Most Recent Change</SelectItem>
                  <SelectItem value="alphabetical">Alphabetical</SelectItem>
                </SelectContent>
              </Select>
            </div>
        </div>
      </div>

      {newsItems.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {enrichedNewsItems.length === 0 ? "No news items found" : `No news items found for ${selectedQuarter}`}
          </p>
          <p className="text-gray-400 text-sm mt-2">
            {enrichedNewsItems.length === 0
              ? "News items will appear here when pricing changes are detected"
              : "Try selecting a different quarter or 'All Quarters' to see more items"
            }
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {newsItems.map((item) => (
              <div 
                key={item.id} 
                className={`bg-white rounded-lg border shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer ${
                  !item.published ? "border-orange-200 bg-orange-50/30" : "border-gray-200"
                }`}
                onClick={() => openNewsViewer(item, true)}
              >
                <div className="flex items-start justify-between gap-6">
                  {/* Left Side - Company Info */}
                  <div className="flex items-start gap-4 min-w-[280px]">
                    {item.logo_url && (
                      <img
                        src={item.logo_url}
                        alt={item.slug}
                        className="w-12 h-12 object-contain rounded-lg border flex-shrink-0"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 capitalize mb-2">
                        {item.slug.replace(/\./g, ' ').replace(/-/g, ' ')}
                      </h3>
                      
                      {/* Date and Quarter */}
                      <div className="flex items-center gap-3">
                        <div className="text-sm text-gray-600">
                          {item.before_version ? (
                            <span>
                              {formatVersionAsDate(item.before_version)} → {formatVersionAsDate(item.version)}
                            </span>
                          ) : (
                            formatVersionAsDate(item.version)
                          )}
                        </div>
                        {item.quarter && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span className="text-sm font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded-full">
                              {formatQuarter(item.quarter).quarter}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Change Type Indicators */}
                      {(item.price_change || item.plan_change || item.metric_change || item.discount_change || item.features_change) && (
                        <div className="flex items-center gap-2 mt-2">
                          {renderChangeTypeIndicators(item, false)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Side - Content */}
                  <div className="flex-1 min-w-0">
                    {/* Hook */}
                    {item.hook && (
                      <div className="mb-3">
                        <h4 className="text-base font-medium text-gray-800 leading-snug">
                          {item.hook}
                        </h4>
                      </div>
                    )}
                    
                    {/* Description */}
                    {item.description && (
                      <div className="mb-3">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 cursor-help">
                              {item.description}
                            </p>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-md text-base p-3">
                            <p>{item.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                    
                    {/* Meta info */}
                    <div className="text-sm text-gray-500">
                      {formatDate(item.created_at)}
                    </div>
                  </div>

                  {/* Status and Actions */}
                  <div className="flex items-center gap-3">
                    {/* Status */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Verified Status */}
                      {item.verified ? (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                          <Badge variant="default" className="text-xs bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                            Verified
                          </Badge>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <div className="h-4 w-4 rounded-full border-2 border-gray-300"></div>
                          <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600 hover:bg-gray-100">
                            Unverified
                          </Badge>
                        </div>
                      )}

                      {/* Published Status */}
                      {item.published ? (
                        <div className="flex items-center gap-1">
                          <Globe className="h-4 w-4 text-blue-500" />
                          <Badge variant="default" className="text-xs bg-blue-100 text-blue-800 hover:bg-blue-100">
                            Published
                          </Badge>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-orange-500" />
                          <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800 hover:bg-orange-100">
                            Draft
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Visual Button - Removed */}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {!item.published ? null : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                            className="flex items-center text-blue-600 border-blue-300 hover:bg-blue-50"
                            title="View published news"
                          >
                            <a
                              href={`https://preview--pricingsaas-frontend.lovable.app/news/${item.slug}/${item.version}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Globe className="h-4 w-4" />
                            </a>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              publishNews(item.id, 'unpublish');
                            }}
                            disabled={publishingItems.has(item.id)}
                            className="flex items-center text-orange-600 border-orange-300 hover:bg-orange-50"
                            title="Unpublish this news item"
                          >
                            {publishingItems.has(item.id) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Undo2 className="h-4 w-4" />
                            )}
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex items-center gap-2"
                      >
                        <a href={`https://pricingsaas.com/news/${item.slug}/${item.version}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(item);
                        }}
                        disabled={deletingItems.has(item.id)}
                        className="flex items-center text-red-600 border-red-300 hover:bg-red-50"
                        title="Delete this news item"
                      >
                        {deletingItems.has(item.id) ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination controls */}
          <div className="flex items-center justify-between mt-6 border-t pt-4">
            <div className="text-sm text-gray-500">
              Showing {newsItems.length} news items (Page {currentPage})
              {(selectedQuarter === "last_14_days" || selectedQuarter === "last_month" || selectedChangeTypes.size > 0) && (
                <span className="text-gray-400 ml-1">(filtered)</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handlePreviousPage}
                disabled={!data?.hasPrevPage || currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {currentPage}
              </span>
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={!data?.hasNextPage}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {/* News Viewer Modal */}
      <NewsViewerModal
        item={selectedNewsItem}
        open={modalOpen}
        onOpenChange={setModalOpen}
        onItemUpdate={handleNewsItemUpdate}
        onItemDelete={() => {
          // Refetch data after deletion
          refetch();
        }}
        startInEditMode={startInEditMode}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationDialog
        isOpen={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={handleDeleteConfirm}
        title="Delete News Item"
        description={`Are you sure you want to delete this news item for ${itemToDelete?.slug?.replace(/-/g, ' ')} - ${itemToDelete?.version}? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      </div>
    </TooltipProvider>
  );
};

export default News;